"use client";

import React, { useState, useEffect } from "react";
import { X, Search, ChevronLeft, ChevronRight, Plus, MoreVertical, AlertCircle, ChevronDown } from "lucide-react";
import PosPaymentStep from "../PosPaymentStep";
import CustomerDetailsDrawer from "../customers/CustomerDetailsDrawer";
import AllergyModal, { AllergyData } from "../customers/AllergyModal";
import PatchTestModal, { PatchTestData } from "../customers/PatchTestModal";
import NotesModal, { NoteData } from "../customers/NotesModal";

interface Service {
  id: number;
  name: string;
  duration: string;
  price: number;
  provider: string;
  category: string;
}

interface SelectedService extends Service {
  time: string;
  warnings?: number;
}

type BookingDrawerProps = {
  isOpen: boolean;
  customerName: string;
  customerEmail: string;
  customerInitial: string;
  onClose: () => void;
};

export default function BookingDrawer({
  isOpen,
  customerName,
  customerEmail,
  customerInitial,
  onClose,
}: BookingDrawerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [currentView, setCurrentView] = useState<"services" | "booking">("services");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [selectedDate, setSelectedDate] = useState("Tue 30 Sep");
  const [selectedTime, setSelectedTime] = useState("8:30pm");
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [tip, setTip] = useState(0);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);

  // Modal states
  const [allergyModalOpen, setAllergyModalOpen] = useState(false);
  const [patchTestModalOpen, setPatchTestModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  // Data states
  const [allergies, setAllergies] = useState<AllergyData[]>([
    {
      id: 1,
      type: "non-drug",
      name: "asd",
      reaction: "Acute kidney failure",
      severity: "mild",
      note: "asd",
    },
  ]);
  const [patchTests, setPatchTests] = useState<PatchTestData[]>([
    {
      id: 1,
      title: "asd",
      tested: "25 September 2025",
      testedBy: "Amir Mukhtar",
      status: "pending",
      description: "asd",
      expiresOn: "25 Mar 2026",
    },
  ]);
  const [notes, setNotes] = useState<NoteData[]>([]);

  // Mock customer data - replace with actual data from props or API
  const customerData = {
    id: 1,
    name: customerName,
    email: customerEmail,
    mobile: "+44 32532424242",
    created: "15 Sep 2025",
  };

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
      setCurrentView("services");
      setSelectedServices([]);
      setSearchQuery("");
      setShowPaymentStep(false);
      setTip(0);
      setShowCustomerDetails(false);
      setAllergyModalOpen(false);
      setPatchTestModalOpen(false);
      setNotesModalOpen(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const allServices: Service[] = [
    { id: 1, name: "Blow Dry", duration: "35min", price: 35, provider: "Amir Mukhtar", category: "Recently booked" },
    { id: 2, name: "Blow Dry", duration: "35min", price: 35, provider: "Amir Mukhtar", category: "Recently booked" },
    { id: 3, name: "Haircut", duration: "45min", price: 40, provider: "Amir Mukhtar", category: "Hair & styling" },
    { id: 4, name: "Hair Color", duration: "1h 15min", price: 57, provider: "Amir Mukhtar", category: "Hair & styling" },
    { id: 5, name: "Blow Dry", duration: "35min", price: 35, provider: "Amir Mukhtar", category: "Hair & styling" },
    { id: 6, name: "Balayage", duration: "2h 30min", price: 150, provider: "Amir Mukhtar", category: "Hair & styling" },
    { id: 7, name: "Manicure", duration: "30min", price: 25, provider: "Amir Mukhtar", category: "Nails" },
    { id: 8, name: "Pedicure", duration: "45min", price: 35, provider: "Amir Mukhtar", category: "Nails" },
    { id: 9, name: "Facial", duration: "1h", price: 60, provider: "Amir Mukhtar", category: "Skincare" },
    { id: 10, name: "Massage", duration: "1h 30min", price: 80, provider: "Amir Mukhtar", category: "Wellness" },
  ];

  // Filter services based on search query
  const filteredServices = allServices.filter(service =>
    service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const recentlyBooked = filteredServices.filter(s => s.category === "Recently booked");
  const hairStyling = filteredServices.filter(s => s.category === "Hair & styling");
  const otherServices = filteredServices.filter(s =>
    s.category !== "Recently booked" && s.category !== "Hair & styling"
  );

  // Group other services by category
  const groupedOtherServices = otherServices.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, Service[]>);

  const handleServiceSelect = (service: Service) => {
    const newService: SelectedService = {
      ...service,
      time: selectedTime,
      warnings: 2
    };
    setSelectedServices([...selectedServices, newService]);
    setCurrentView("booking");
  };

  const handleCheckout = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setShowPaymentStep(true);
    }, 300);
  };

  const handleClosePaymentStep = () => {
    setShowPaymentStep(false);
    setTimeout(() => {
      setIsVisible(true);
      setIsClosing(false);
    }, 100);
  };

  const handleGoToFinalPayment = () => {
    setShowPaymentStep(false);
    setIsVisible(false);
    onClose();
    console.log("Payment completed - proceeding to final confirmation");
  };

  const handleViewProfile = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setShowCustomerDetails(true);
    }, 300);
  };

  const handleCloseCustomerDetails = () => {
    setShowCustomerDetails(false);
    setTimeout(() => {
      setIsVisible(true);
      setIsClosing(false);
    }, 100);
  };

  const handleActionsClick = (action: string) => {
    setActionsDropdownOpen(false);
    if (action === "Allergies") {
      setAllergyModalOpen(true);
    } else if (action === "Notes") {
      setNotesModalOpen(true);
    } else if (action === "Patch Test") {
      setPatchTestModalOpen(true);
    }
  };

  // Allergy Handlers
  const handleSaveAllergy = (allergyData: AllergyData) => {
    setAllergies([...allergies, { ...allergyData, id: Date.now() }]);
    setAllergyModalOpen(false);
  };

  const handleCloseAllergyModal = () => {
    setAllergyModalOpen(false);
  };

  // Patch Test Handlers
  const handleSavePatchTest = (patchTestData: PatchTestData) => {
    setPatchTests([...patchTests, { ...patchTestData, id: Date.now() }]);
    setPatchTestModalOpen(false);
  };

  const handleDeletePatchTest = (id: number) => {
    setPatchTests(patchTests.filter((p) => p.id !== id));
  };

  const handleClosePatchTestModal = () => {
    setPatchTestModalOpen(false);
  };

  // Notes Handlers
  const handleSaveNote = (noteData: NoteData) => {
    setNotes([...notes, { ...noteData, id: Date.now() }]);
    setNotesModalOpen(false);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
  };

  const totalPrice = selectedServices.reduce((sum, service) => sum + service.price, 0);

  if (!isOpen && !isVisible && !showPaymentStep && !showCustomerDetails) return null;

  return (
    <>
      {isVisible && !showPaymentStep && !showCustomerDetails && (
        <div className="fixed inset-0 z-[110] flex justify-end">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
              isVisible && !isClosing ? "opacity-100" : "opacity-0"
            }`}
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Drawer Panel */}
          <div
            className={`relative w-[85vw] max-w-5xl h-screen transform transition-transform duration-300 ease-out ${
              isVisible && !isClosing ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute -left-16 top-10 w-11 h-11 flex items-center justify-center rounded-full bg-white text-purple-700 shadow-md hover:bg-purple-100 transition z-10"
              aria-label="Close"
            >
              <X size={22} strokeWidth={2.5} />
            </button>

            {/* Drawer Content */}
            <div className="relative z-50 w-full h-screen bg-white shadow-2xl flex">
              {/* Customer Info Sidebar */}
              <div className="w-[380px] border-r border-gray-200 flex flex-col">
                {/* Customer Profile */}
                <div className="p-8 flex flex-col items-center border-b border-gray-200">
                  <div className="w-24 h-24 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-3xl font-semibold mb-4">
                    {customerInitial}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{customerName}</h3>
                  <p className="text-sm text-gray-600">{customerEmail}</p>

                  <div className="flex gap-2 mt-6 w-full">
                    <div className="relative flex-1">
                      <button
                        onClick={() => setActionsDropdownOpen(!actionsDropdownOpen)}
                        className="w-full flex items-center justify-center gap-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
                      >
                        Actions <ChevronDown className="w-4 h-4" />
                      </button>

                      {/* Dropdown Menu */}
                      {actionsDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-[115]"
                            onClick={() => setActionsDropdownOpen(false)}
                          />
                          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-[120] overflow-hidden">
                            <button
                              onClick={() => handleActionsClick("Allergies")}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition"
                            >
                              Add Allergy
                            </button>
                            <button
                              onClick={() => handleActionsClick("Notes")}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition border-t border-gray-100"
                            >
                              Add Note
                            </button>
                            <button
                              onClick={() => handleActionsClick("Patch Test")}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition border-t border-gray-100"
                            >
                              Add Patch Test
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={handleViewProfile}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
                    >
                      View profile
                    </button>
                  </div>
                </div>

                {/* Customer Details */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-4">
                    {/* Display Allergies */}
                    {allergies.map((allergy) => (
                      <div key={allergy.id} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 rounded-full bg-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 capitalize">{allergy.severity}</p>
                          <p className="text-xs text-gray-600">{allergy.severity} allergy · {allergy.reaction}</p>
                        </div>
                      </div>
                    ))}

                    {/* Display Patch Tests */}
                    {patchTests.map((test) => (
                      <div key={test.id} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                        <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <AlertCircle className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{test.title}</p>
                          <p className="text-xs text-gray-600">Expires {test.expiresOn} · {test.status}</p>
                        </div>
                      </div>
                    ))}

                    <div className="flex items-center gap-2 text-sm text-gray-600 py-2 border-t border-gray-200 mt-4 pt-4">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Created 15 Sep 2025
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Content Area */}
              <div className="flex-1 flex flex-col">
                {currentView === "services" ? (
                  <>
                    {/* Header */}
                    <div className="px-8 py-6 border-b border-gray-200">
                      <button
                        onClick={handleClose}
                        className="flex items-center gap-2 text-gray-700 hover:text-gray-900 mb-4"
                      >
                        <ChevronLeft size={20} />
                        <span className="text-sm font-medium">Back</span>
                      </button>
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Add a service</h2>

                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          placeholder="Search by service name"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Services List */}
                    <div className="flex-1 overflow-y-auto px-8 py-6">
                      {searchQuery && filteredServices.length === 0 ? (
                        <div className="text-center py-12">
                          <p className="text-gray-500">No services found matching "{searchQuery}"</p>
                        </div>
                      ) : (
                        <>
                          {/* Recently Booked */}
                          {recentlyBooked.length > 0 && (
                            <div className="mb-8">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Recently booked <span className="text-gray-500 font-normal">{recentlyBooked.length}</span>
                                </h3>
                                <div className="flex gap-2">
                                  <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
                                    <ChevronLeft size={18} className="text-gray-600" />
                                  </button>
                                  <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
                                    <ChevronRight size={18} className="text-gray-600" />
                                  </button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                {recentlyBooked.map((service) => (
                                  <button
                                    key={service.id}
                                    onClick={() => handleServiceSelect(service)}
                                    className="p-5 border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-md transition text-left"
                                  >
                                    <h4 className="font-semibold text-gray-900 mb-1">{service.name}</h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                      29 Sep 2025 · {service.provider}
                                    </p>
                                    <p className="text-sm font-medium text-gray-900">PKR {service.price}</p>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Hair & Styling */}
                          {hairStyling.length > 0 && (
                            <div className="mb-8">
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Hair & styling <span className="text-gray-500 font-normal">{hairStyling.length}</span>
                              </h3>
                              <div className="space-y-3">
                                {hairStyling.map((service) => (
                                  <button
                                    key={service.id}
                                    onClick={() => handleServiceSelect(service)}
                                    className="w-full flex items-center justify-between p-4 border-l-4 border-blue-400 hover:bg-gray-50 transition"
                                  >
                                    <div className="text-left">
                                      <h4 className="font-semibold text-gray-900">{service.name}</h4>
                                      <p className="text-sm text-gray-600">{service.duration}</p>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-900">PKR {service.price}</p>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Other Service Categories */}
                          {Object.entries(groupedOtherServices).map(([category, services]) => (
                            <div key={category} className="mb-8">
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                {category} <span className="text-gray-500 font-normal">{services.length}</span>
                              </h3>
                              <div className="space-y-3">
                                {services.map((service) => (
                                  <button
                                    key={service.id}
                                    onClick={() => handleServiceSelect(service)}
                                    className="w-full flex items-center justify-between p-4 border-l-4 border-blue-400 hover:bg-gray-50 transition"
                                  >
                                    <div className="text-left">
                                      <h4 className="font-semibold text-gray-900">{service.name}</h4>
                                      <p className="text-sm text-gray-600">{service.duration}</p>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-900">PKR {service.price}</p>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Booking View Header */}
                    <div className="px-8 py-6 border-b border-gray-200">
                      <div className="flex items-center gap-3 mb-4">
                        <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-medium">
                          {selectedDate}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      <p className="text-sm text-gray-600">{selectedTime} · Doesn't repeat</p>
                    </div>

                    {/* Services Section */}
                    <div className="flex-1 overflow-y-auto px-8 py-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Services</h3>

                      {selectedServices.map((service, idx) => (
                        <div key={idx} className="mb-4 p-5 border-l-4 border-blue-400 bg-white">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="text-lg font-semibold text-gray-900">{service.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {service.time} · {service.duration} · {service.provider}
                              </p>
                            </div>
                            <p className="text-lg font-semibold text-gray-900">PKR {service.price}</p>
                          </div>
                          {service.warnings && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg w-fit">
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-900">{service.warnings} warnings</span>
                            </div>
                          )}
                        </div>
                      ))}

                      <button
                        onClick={() => setCurrentView("services")}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                      >
                        <Plus size={18} />
                        <span className="text-sm font-medium">Add service</span>
                      </button>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 px-8 py-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-lg font-semibold text-gray-900">Total</span>
                        <span className="text-lg font-bold text-gray-900">PKR {totalPrice}</span>
                      </div>
                      <div className="flex gap-3">
                        <button className="w-12 h-12 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                          <MoreVertical size={20} className="text-gray-700" />
                        </button>
                        <button
                          onClick={handleCheckout}
                          className="flex-1 px-6 py-3 bg-white border border-gray-300 text-gray-900 font-medium rounded-lg hover:bg-purple-100 transition"
                        >
                          Checkout
                        </button>
                        <button className="flex-1 px-6 py-3 bg-purple-700 text-white font-medium rounded-lg hover:bg-purple-800 transition">
                          Save
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Drawer */}
      {showCustomerDetails && (
        <CustomerDetailsDrawer
          customer={customerData}
          isOpen={showCustomerDetails}
          onClose={handleCloseCustomerDetails}
        />
      )}

      {/* Modals - These need to be outside the drawer with higher z-index */}
      {allergyModalOpen && (
        <div className="relative z-[150]">
          <AllergyModal
            isOpen={allergyModalOpen}
            onClose={handleCloseAllergyModal}
            onSave={handleSaveAllergy}
            editData={null}
          />
        </div>
      )}

      {patchTestModalOpen && (
        <div className="relative z-[150]">
          <PatchTestModal
            isOpen={patchTestModalOpen}
            onClose={handleClosePatchTestModal}
            onSave={handleSavePatchTest}
            onDelete={handleDeletePatchTest}
            editData={null}
          />
        </div>
      )}

      {notesModalOpen && (
        <div className="relative z-[150]">
          <NotesModal
            isOpen={notesModalOpen}
            onClose={handleCloseNotesModal}
            onSave={handleSaveNote}
            editData={null}
          />
        </div>
      )}

      {/* POS Payment Step */}
      {showPaymentStep && selectedServices.length > 0 && (
        <PosPaymentStep
          selectedService={{
            id: selectedServices[0].id.toString(),
            name: selectedServices.map(s => s.name).join(", "),
            duration: selectedServices.map(s => s.duration).join(", "),
            price: `PKR ${totalPrice}`,
          }}
          selectedCustomer={customerName}
          appointmentTime={`${selectedDate} ${selectedTime}`}
          tip={tip}
          setTip={setTip}
          goToFinalPayment={handleGoToFinalPayment}
          onClose={handleClosePaymentStep}
          mode="paynow"
        />
      )}
    </>
  );
}
